import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  BadgeCheck,
  Copy,
  Download,
  History,
  IdCard,
  KeyRound,
  Pencil,
  Phone,
  Printer,
  ShieldOff,
  Trash2,
  UserRoundX,
  Users,
  Wallet,
} from 'lucide-react'
import { useState } from 'react'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useAccessCredential,
  useConsentHistory,
  useCredentialMutations,
  useCurrentPermissions,
  useGymMemberExports,
  useMemberAccessEvents,
  useGymMember,
  useGymMemberMutations,
  useGymMembers,
  useLocations,
  useMemberDocumentMutations,
  useMemberDocuments,
  useMemberWallet,
  useMemberWalletMovements,
} from '@/api/queries'
import type {
  ConsentChannel,
  ConsentKind,
  GymMember,
  GymMemberAccessCredentialSecret,
  GymMemberClaimCode,
  GymMemberContact,
  GymMemberRelation,
  GymMemberSportCard,
  MemberDocument,
  MemberDocumentKind,
  PrintedCredentialToken,
  WalletMovementKind,
} from '@/api/types'
import { CONSENT_CHANNELS, CONSENT_KINDS, MEMBER_DOCUMENT_KINDS } from '@/api/types'
import { DOCUMENT_PERMISSIONS } from '@/api/endpoints/nutrition'
import { PageHeader } from '@/components/layout/app-shell'
import { TasksSection } from '@/features/crm/tasks-section'
import { MessagesSection } from '@/features/crm/messages-section'
import { EditGymMemberDialog } from '@/features/members/edit-gym-member-dialog'
import { MemberSalesSection } from '@/features/members/sales-section'
import { TimelineSection } from '@/features/crm/timeline-section'
import { ServiceCreditsSection } from '@/features/gym-subscriptions/service-credits-section'
import { SubscriptionsSection } from '@/features/gym-subscriptions/subscriptions-section'
import { formatDate, formatDateTime, formatMoney } from '@/lib/format'
import { notify } from '@/lib/toast'

/**
 * La scheda dell'iscritto — ANA-06.
 *
 * ## Una chiamata, sette sezioni
 *
 * `GET …/gym-members/{id}` porta anagrafica, contatti, legami, consensi correnti,
 * tesseramento corrente e iscrizioni attive **insieme**: la schermata si apre a ogni
 * persona che arriva al banco, e comporla con sei richieste vorrebbe dire farla
 * comparire a pezzi mentre qualcuno aspetta. Le sotto-risorse si scrivono invece una
 * per una, e ogni scrittura restituisce ciò che ha creato.
 *
 * ## Le tre cose che questa pagina non lascia dedurre
 *
 *  1. **il consenso non è un interruttore.** Il registro è append-only: revocare
 *     significa registrare una riga nuova, e la UI mostra due pulsanti — «concedi» e
 *     «revoca» — invece di uno switch che sovrascriverebbe la storia;
 *  2. **il segreto della credenziale esce una volta sola.** La finestra che lo mostra
 *     non si chiude cliccando fuori, come quella dei codici di recupero del secondo
 *     fattore;
 *  3. **anonimizzare non è cancellare, e non torna indietro.** Chiede il cognome
 *     scritto a mano, e dopo la scheda diventa di sola lettura.
 */
export const Route = createFileRoute('/_app/iscritti/$gymMemberId')({
  component: GymMemberDetailPage,
})

const CONSENT_LABELS: Record<ConsentKind, string> = {
  data_processing: 'Trattamento dei dati',
  marketing: 'Marketing',
  image: 'Immagine',
  third_party: 'Terze parti',
}

const DOCUMENT_KIND_LABELS: Record<MemberDocumentKind, string> = {
  workout_plan: 'Scheda',
  meal_plan: 'Piano alimentare',
  other: 'Altro',
}

const CHANNEL_LABELS: Record<ConsentChannel, string> = {
  paper: 'Cartaceo',
  app: 'App',
  panel: 'Pannello',
  web: 'Web',
}

function GymMemberDetailPage() {
  const { gymMemberId } = Route.useParams()
  const member = useGymMember(gymMemberId)
  const permissions = useCurrentPermissions()
  const exports = useGymMemberExports()
  const canExport = permissions.has('gym_member:export')
  const canWrite = permissions.has('gym_member:write')
  const [editing, setEditing] = useState(false)

  if (member.isError && !member.data) {
    return (
      <ErrorState
        message={errorMessage(member.error)}
        requestId={member.error instanceof ApiError ? member.error.requestId : undefined}
        onRetry={() => void member.refetch()}
      />
    )
  }

  if (member.isPending || !member.data) {
    return (
      <>
        <Skeleton className="mb-4 h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </>
    )
  }

  const data = member.data.data
  // L'`ETag` della lettura, che torna in `If-Match` su ogni scrittura dell'anagrafica.
  // `null` non e' un guasto: se il backend smettesse di esporlo in
  // `Access-Control-Expose-Headers` il browser non lo vedrebbe, e le scritture
  // tornerebbero a «vince l'ultima» invece di fallire — degradare, non rompere.
  const etag = member.data.etag
  const anonymized = data.anonymizedAt !== null

  /**
   * Il documento di portabilità, scaricato come file.
   *
   * Non si mostra in pagina: è la risposta a una richiesta dell'interessato, e ciò che serve
   * è un file da consegnargli. Il nome arriva dal server e porta la data.
   */
  async function downloadPortability(): Promise<void> {
    const { blob, filename } = await exports.portability.mutateAsync(data.id)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="mb-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/iscritti">
            <ArrowLeft />
            Tutti gli iscritti
          </Link>
        </Button>
      </div>

      <PageHeader
        title={`${data.lastName} ${data.firstName}`}
        description={
          data.memberNumber !== null
            ? `Tessera ${data.memberNumber} · iscritto dal ${formatDate(data.createdAt)}`
            : `Iscritto dal ${formatDate(data.createdAt)}`
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {anonymized ? (
              <Badge variant="neutral">Anonimizzata il {formatDate(data.anonymizedAt)}</Badge>
            ) : (
              <Badge variant={data.status === 'active' ? 'success' : 'warning'}>
                {data.status === 'active'
                  ? 'Attivo'
                  : data.status === 'suspended'
                    ? 'Sospeso'
                    : 'Cessato'}
              </Badge>
            )}
            {/*
              Portabilità (ANA-08). Nascosto su un'anagrafica anonimizzata perché là la rotta
              risponde `409`: non ci sono più dati da portare via, e offrire il pulsante
              significherebbe promettere un documento che non arriva.
            */}
            {canExport && !anonymized ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void downloadPortability()}
                loading={exports.portability.isPending}
              >
                <Download className="size-4" />
                Esporta i dati
              </Button>
            ) : null}
            {/*
              ANA-01. Nascosto su un'anagrafica anonimizzata per la stessa ragione
              dell'esportazione: là la scrittura risponde `409`, e offrire il pulsante
              prometterebbe una modifica che non avviene.
            */}
            {canWrite && !anonymized ? (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="size-4" />
                Modifica
              </Button>
            ) : null}
          </div>
        }
      />

      {anonymized ? (
        <Alert variant="neutral" className="mb-4" title="Questa scheda è chiusa">
          L'anagrafica è stata anonimizzata: l'identità non c'è più, le iscrizioni e i pagamenti
          restano per obbligo fiscale. Nessuna scrittura è più possibile, e non è un errore.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <IdentitySection member={data} etag={etag} disabled={anonymized} />
        <ContactsSection member={data} disabled={anonymized} />
        <RelationsSection member={data} disabled={anonymized} />
        <ConsentsSection member={data} disabled={anonymized} />
        <SportCardSection member={data} disabled={anonymized} />
        <CredentialSection member={data} disabled={anonymized} />
        <SubscriptionsSection gymMemberId={gymMemberId} disabled={anonymized} />
        <AccessesSection gymMemberId={gymMemberId} />
        {/*
          SRV-04. Sta fra gli abbonamenti e il borsellino perché è la stessa famiglia — cosa
          questa persona ha diritto di usare —, e perché la domanda che porta qui («perché me
          ne restano tre e non quattro?») si fa davanti a lei.
        */}
        <Section
          title="Carnet"
          description="Gli ingressi che le restano, e perché. Il registro non si riscrive: una correzione è un movimento in più."
        >
          <ServiceCreditsSection gymMemberId={gymMemberId} />
        </Section>
        <WalletSection gymMemberId={gymMemberId} />
        {/*
          ANA-06. Accanto al borsellino perché è la stessa famiglia — il denaro di questa
          persona — e perché «ha pagato tutto?» si chiede con lei davanti.
        */}
        <Section
          title="Acquisti"
          description="Cosa ha comprato e cosa deve ancora. Lo vede chi ha il permesso sulle vendite."
        >
          <MemberSalesSection gymMemberId={gymMemberId} />
        </Section>
        <DocumentsSection gymMemberId={gymMemberId} disabled={anonymized} />
        {/*
          CRM-08. Sta nella scheda della persona e non in un elenco a parte: «cosa
          ci siamo detti» si legge davanti a chi si ha al telefono, e un secondo
          posto in cui cercarlo significherebbe non cercarlo.
        */}
        <Section
          title="Note e attività"
          description="Cosa ci siamo detti, e cosa resta da fare. Senza una data è una nota."
        >
          <TasksSection gymMemberId={gymMemberId} />
        </Section>

        {/*
          CRM-03. Sta accanto alle note e non in una pagina a parte, per la stessa ragione:
          «perché non gli è arrivato niente» si chiede davanti alla persona che lo sta
          chiedendo, spesso al telefono con lei.
        */}
        <Section
          title="Comunicazioni"
          description="Le email inviate, comprese quelle non partite: è la risposta a «perché non gli è arrivato niente»."
        >
          <MessagesSection gymMemberId={gymMemberId} />
        </Section>

        {/*
          CRM-07. Sta qui e non in una pagina propria per la stessa ragione delle
          note: «cos'è successo con questa persona» si chiede davanti alla sua
          scheda. È un solo flusso e non sei elenchi cuciti — la sezione «Accessi»
          resta perché risponde a un'altra domanda, «chi è entrato e quando», che si
          fa dal registro e non dalla persona.
        */}
        <Section
          title="Cronologia"
          description="Un solo flusso di ciò che è successo, dal più recente. Gli eventi non si correggono: una correzione è un evento nuovo."
        >
          <TimelineSection gymMemberId={gymMemberId} />
        </Section>
      </div>

      {!anonymized ? <AnonymizeSection member={data} /> : null}

      {editing ? (
        <EditGymMemberDialog member={data} etag={etag} onClose={() => setEditing(false)} />
      ) : null}
    </>
  )
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="border-border bg-card rounded-lg border p-4">
      <h2 className="font-semibold">{title}</h2>
      {description !== undefined ? (
        <p className="text-muted-foreground mb-3 text-xs">{description}</p>
      ) : (
        <div className="mb-3" />
      )}
      {children}
    </section>
  )
}

/**
 * La sede di riferimento, **modificabile** — ANA-01, TEN-06.
 *
 * ## Perché è l'unico campo modificabile di questa sezione
 *
 * Il resto dei «Dati» è in sola lettura, e non per scelta: nel pannello **non esiste** un
 * modulo di modifica dell'anagrafica — `PATCH /organization/gym-members/{id}` è nel livello
 * API e nessuna schermata la chiama. È un buco che precede questo giro e che l'audit delle
 * rotte non può vedere, perché quel percorso *compare* nel sorgente (in `endpoints/`) e la
 * sonda conta le occorrenze, non i chiamanti.
 *
 * Qui però un campo che si può solo impostare alla registrazione sarebbe lo stesso difetto
 * dello sconto che si crea e non si corregge: la sede la sbaglia chi registra di corsa al
 * banco, e correggerla dovrebbe costare un clic — non una riregistrazione.
 *
 * ## Con `If-Match`, da quando il modulo esiste
 *
 * Questa riga è nata senza: `GET` della scheda usava `api.get` e buttava via l'`ETag`,
 * quindi valeva l'ultima scrittura — accettabile su un campo solo, dove due operatori che
 * scelgono sedi diverse producono un disaccordo visibile e non una perdita silenziosa.
 * Il modulo di modifica ha portato la scheda taggata, e allora questa riga manda l'`ETag`
 * anche lei: due strade che scrivono lo stesso campo con due discipline diverse sono un
 * modo di scoprire, un giorno, che la protezione dipendeva da quale pulsante era stato
 * premuto.
 */
function PrimaryLocationRow({
  member,
  etag,
  disabled,
}: {
  member: GymMember
  etag: string | null
  disabled: boolean
}) {
  const locations = useLocations()
  const rows = locations.data ?? []
  const { update } = useGymMemberMutations(member.id)

  // Con una sede sola non c'è niente da scegliere, e nemmeno da mostrare: sarebbe una riga
  // che dice «Bologna Centro» su ogni scheda di una palestra che ha solo Bologna Centro.
  if (rows.length < 2) return null

  if (disabled) return <Row label="Sede di riferimento" value={member.primaryLocationName} />

  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm">
      <span className="text-muted-foreground">Sede di riferimento</span>
      <div className="w-52">
        <Select
          value={member.primaryLocationId ?? ''}
          aria-label="Sede di riferimento"
          disabled={update.isPending}
          onValueChange={(value) => {
            void update
              .mutateAsync({ input: { primaryLocationId: value === '' ? null : value }, etag })
              .then(() => {
                notify({ message: 'Sede aggiornata.', variant: 'success' })
              })
              .catch((cause: unknown) => {
                notify({ message: errorMessage(cause), variant: 'danger' })
              })
          }}
          items={[
            { value: '', label: 'Nessuna' },
            ...rows.map((location) => ({ value: location.id, label: location.name })),
          ]}
        />
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value === null || value === '' ? '—' : value}</span>
    </div>
  )
}

function IdentitySection({
  member,
  etag,
  disabled,
}: {
  member: GymMember
  etag: string | null
  disabled: boolean
}) {
  return (
    <Section title="Dati" description="Quello che l'elenco non mostra.">
      <PrimaryLocationRow member={member} etag={etag} disabled={disabled} />
      <Row label="Codice fiscale" value={member.taxCode} />
      <Row label="Nato il" value={member.bornOn === null ? null : formatDate(member.bornOn)} />
      <Row
        label="Luogo di nascita"
        value={
          member.birthPlace === null
            ? null
            : [member.birthPlace, member.birthProvince].filter(Boolean).join(' ')
        }
      />
      <Row label="Email" value={member.email} />
      <Row label="Telefono" value={member.phone} />
      <Row
        label="Residenza"
        value={
          [member.addressLine, member.postalCode, member.city, member.province]
            .filter(Boolean)
            .join(', ') || null
        }
      />
      <Row label="Account collegato" value={member.userId === null ? 'nessuno' : 'sì'} />

      {member.internalNotes !== null ? (
        <>
          <p className="text-muted-foreground mt-3 text-xs font-medium">Note interne</p>
          <p className="text-sm">{member.internalNotes}</p>
        </>
      ) : null}

      {/* ⚠️ L'avvertenza sta **dove si scrive**, non in un documento: è il campo in cui
          i dati sanitari rientrano dalla finestra, e chi digita «ginocchio operato» non
          sta leggendo una policy. */}
      <Alert variant="warning" className="mt-3">
        Le note interne non sono il posto per informazioni sulla salute: «ginocchio operato» o
        «cardiopatico» sono dati particolari, e il sistema non è ancora attrezzato per conservarli.
      </Alert>
    </Section>
  )
}

function ContactsSection({ member, disabled }: { member: GymMember; disabled: boolean }) {
  const { addContact, removeContact } = useGymMemberMutations(member.id)
  const [value, setValue] = useState('')
  const [label, setLabel] = useState('')
  const [emergency, setEmergency] = useState(false)

  return (
    <Section
      title="Contatti"
      description="I principali stanno nei dati; qui il resto, e chi chiamare se succede qualcosa in sala."
    >
      {member.contacts.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nessun contatto aggiuntivo.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {member.contacts.map((contact) => (
            <ContactRow
              key={contact.id}
              gymMemberId={member.id}
              contact={contact}
              disabled={disabled}
              onRemove={() => removeContact.mutate(contact.id)}
              removing={removeContact.isPending}
            />
          ))}
        </ul>
      )}

      {!disabled ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_1fr_auto]">
          <Field label="Numero o email">
            {(props) => (
              <Input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
            )}
          </Field>
          <Field label="Etichetta">
            {(props) => (
              <Input
                {...props}
                value={label}
                placeholder="Mamma"
                onChange={(e) => setLabel(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-end gap-2">
            <Checkbox
              className="text-xs"
              checked={emergency}
              onCheckedChange={setEmergency}
              label="emergenza"
            />
            <Button
              size="sm"
              disabled={value.trim() === ''}
              loading={addContact.isPending}
              onClick={() => {
                addContact.mutate(
                  {
                    // Il tipo si deduce da cosa è stato scritto: chiedere «telefono o
                    // email?» accanto a un campo che lo dice già è una domanda in più
                    // al banco.
                    kind: value.includes('@') ? 'email' : 'phone',
                    value: value.trim(),
                    label: label.trim() === '' ? undefined : label.trim(),
                    isEmergency: emergency,
                  },
                  {
                    onSuccess: () => {
                      setValue('')
                      setLabel('')
                      setEmergency(false)
                    },
                  },
                )
              }}
            >
              <Phone />
              Aggiungi
            </Button>
          </div>
        </div>
      ) : null}

      {addContact.isError ? (
        <Alert variant="danger" className="mt-2">
          {errorMessage(addContact.error)}
        </Alert>
      ) : null}
    </Section>
  )
}

/**
 * Una riga di contatto, in lettura o in modifica.
 *
 * La correzione è **in riga e non in una finestra**: un numero sbagliato si scopre
 * mentre lo si legge al banco, e aprire un dialogo per cambiare una cifra costa più
 * dell'errore. `label: null` cancella l'etichetta — nel contract il campo è nullabile
 * di proposito, e mandare stringa vuota lascerebbe un'etichetta vuota invece di nessuna.
 */
function ContactRow({
  gymMemberId,
  contact,
  disabled,
  onRemove,
  removing,
}: {
  gymMemberId: string
  contact: GymMemberContact
  disabled: boolean
  onRemove: () => void
  removing: boolean
}) {
  const { updateContact } = useGymMemberMutations(gymMemberId)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(contact.value)
  const [label, setLabel] = useState(contact.label ?? '')
  const [emergency, setEmergency] = useState(contact.isEmergency)

  function open() {
    setValue(contact.value)
    setLabel(contact.label ?? '')
    setEmergency(contact.isEmergency)
    setEditing(true)
  }

  if (editing) {
    return (
      <li>
        <Card radius="md" padding="sm" className="py-2">
          <div className="grid gap-2 sm:grid-cols-[2fr_1fr_auto]">
            <Field label="Numero o email">
              {(props) => (
                <Input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
              )}
            </Field>
            <Field label="Etichetta">
              {(props) => (
                <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
              )}
            </Field>
            <div className="flex items-end gap-2">
              <Checkbox
                className="text-xs"
                checked={emergency}
                onCheckedChange={setEmergency}
                label="emergenza"
              />
              <Button
                size="sm"
                disabled={value.trim() === ''}
                loading={updateContact.isPending}
                onClick={() => {
                  updateContact.mutate(
                    {
                      contactId: contact.id,
                      input: {
                        kind: value.includes('@') ? 'email' : 'phone',
                        value: value.trim(),
                        label: label.trim() === '' ? null : label.trim(),
                        isEmergency: emergency,
                      },
                    },
                    { onSuccess: () => setEditing(false) },
                  )
                }}
              >
                Salva
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Annulla
              </Button>
            </div>
          </div>
          {updateContact.isError ? (
            <Alert variant="danger" className="mt-2">
              {errorMessage(updateContact.error)}
            </Alert>
          ) : null}
        </Card>
      </li>
    )
  }

  return (
    <li>
      <Card radius="md" padding="sm" className="flex items-center justify-between gap-2 py-2">
        <span className="truncate text-sm">
          {contact.value}
          {contact.label !== null ? (
            <span className="text-muted-foreground"> · {contact.label}</span>
          ) : null}
        </span>
        <div className="flex items-center gap-2">
          {contact.isEmergency ? <Badge variant="warning">Emergenza</Badge> : null}
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            aria-label="Modifica contatto"
            onClick={open}
          >
            <Pencil />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled || removing}
            aria-label="Rimuovi contatto"
            onClick={onRemove}
          >
            <Trash2 />
          </Button>
        </div>
      </Card>
    </li>
  )
}

const RELATION_LABELS: Record<GymMemberRelation['relation'], string> = {
  parent: 'genitore',
  guardian: 'tutore',
  child: 'figlio',
  spouse: 'coniuge',
  sibling: 'fratello o sorella',
  other: 'altro',
}

/**
 * I legami familiari e di tutela — ANA-02.
 *
 * Un legame o punta a un'altra anagrafica di questa palestra, **o** porta il nome di
 * una persona esterna: mai nessuna delle due, e la UI lo impone con un interruttore
 * fra i due modi invece di lasciare compilare entrambi e scoprire il `422` dopo.
 *
 * La scheda mostra solo i legami **posseduti** da questa persona, mai quelli in cui
 * compare come parente di un altro: cercare qui il minore di cui questo adulto è
 * tutore significa guardare la scheda sbagliata.
 */
function RelationsSection({ member, disabled }: { member: GymMember; disabled: boolean }) {
  const { addRelation, removeRelation } = useGymMemberMutations(member.id)
  const [relation, setRelation] = useState<GymMemberRelation['relation']>('parent')
  const [mode, setMode] = useState<'internal' | 'external'>('external')
  const [relatedGymMemberId, setRelatedGymMemberId] = useState('')
  const [externalName, setExternalName] = useState('')
  const [externalPhone, setExternalPhone] = useState('')
  const [guardian, setGuardian] = useState(false)

  const members = useGymMembers({ status: 'active', limit: 100 })
  const candidates = (members.data?.data ?? []).filter((item) => item.id !== member.id)

  const ready = mode === 'internal' ? relatedGymMemberId !== '' : externalName.trim() !== ''

  return (
    <Section
      title="Legami"
      description="Chi risponde per questa persona, e di chi risponde questa persona."
    >
      {member.relations.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nessun legame registrato.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {member.relations.map((item) => (
            <li key={item.id}>
              <Card
                radius="md"
                padding="sm"
                className="flex items-center justify-between gap-2 py-2"
              >
                <span className="truncate text-sm">
                  {item.relatedName ?? '—'}
                  <span className="text-muted-foreground"> · {RELATION_LABELS[item.relation]}</span>
                  {item.externalPhone !== null ? (
                    <span className="text-muted-foreground"> · {item.externalPhone}</span>
                  ) : null}
                </span>
                <div className="flex items-center gap-2">
                  {item.isLegalGuardian ? <Badge variant="warning">Tutore legale</Badge> : null}
                  {item.relatedGymMemberId !== null ? (
                    <Link
                      to="/iscritti/$gymMemberId"
                      params={{ gymMemberId: item.relatedGymMemberId }}
                      className="text-primary-content text-xs underline-offset-2 hover:underline"
                    >
                      apri scheda
                    </Link>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled || removeRelation.isPending}
                    aria-label="Rimuovi legame"
                    onClick={() => removeRelation.mutate(item.id)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {!disabled ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <Field label="Tipo di legame">
            {(props) => (
              <Select
                {...props}
                value={relation}
                onValueChange={(v) => setRelation(v as GymMemberRelation['relation'])}
                items={Object.entries(RELATION_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </Field>
          <Field label="Chi">
            {(props) => (
              <Select
                {...props}
                value={mode}
                onValueChange={(value) => setMode(value as typeof mode)}
                items={[
                  { value: 'external', label: 'Persona esterna' },
                  { value: 'internal', label: 'Un altro iscritto' },
                ]}
              />
            )}
          </Field>
          {mode === 'internal' ? (
            <Field label="Iscritto">
              {(props) => (
                <Select
                  {...props}
                  value={relatedGymMemberId}
                  onValueChange={setRelatedGymMemberId}
                  placeholder="Scegli"
                  items={candidates.map((item) => ({
                    value: item.id,
                    label: `${item.lastName} ${item.firstName}`,
                  }))}
                />
              )}
            </Field>
          ) : (
            <Field label="Nome e cognome">
              {(props) => (
                <Input
                  {...props}
                  value={externalName}
                  onChange={(e) => setExternalName(e.target.value)}
                />
              )}
            </Field>
          )}
          {mode === 'external' ? (
            <Field label="Telefono">
              {(props) => (
                <Input
                  {...props}
                  value={externalPhone}
                  onChange={(e) => setExternalPhone(e.target.value)}
                />
              )}
            </Field>
          ) : null}
          <div className="flex items-end gap-2 sm:col-span-3">
            <Checkbox
              className="text-xs"
              checked={guardian}
              onCheckedChange={setGuardian}
              label="è tutore legale"
            />
            <Button
              size="sm"
              disabled={!ready}
              loading={addRelation.isPending}
              onClick={() =>
                addRelation.mutate(
                  {
                    relation,
                    ...(mode === 'internal'
                      ? { relatedGymMemberId }
                      : {
                          externalName: externalName.trim(),
                          ...(externalPhone.trim() === ''
                            ? {}
                            : { externalPhone: externalPhone.trim() }),
                        }),
                    isLegalGuardian: guardian,
                  },
                  {
                    onSuccess: () => {
                      setRelatedGymMemberId('')
                      setExternalName('')
                      setExternalPhone('')
                      setGuardian(false)
                    },
                  },
                )
              }
            >
              <Users />
              Aggiungi
            </Button>
          </div>
        </div>
      ) : null}

      {addRelation.isError ? (
        <Alert variant="danger" className="mt-2">
          {errorMessage(addRelation.error)}
        </Alert>
      ) : null}
    </Section>
  )
}

function ConsentsSection({ member, disabled }: { member: GymMember; disabled: boolean }) {
  const { addConsent } = useGymMemberMutations(member.id)
  const [kind, setKind] = useState<ConsentKind>('data_processing')
  const [channel, setChannel] = useState<ConsentChannel>('paper')
  const [policyVersion, setPolicyVersion] = useState('')

  const current = new Map(member.currentConsents.map((c) => [c.kind, c]))

  function record(granted: boolean) {
    if (policyVersion.trim() === '') return
    addConsent.mutate({ kind, granted, policyVersion: policyVersion.trim(), channel })
  }

  return (
    <Section
      title="Consensi"
      description="Un registro, non un interruttore: revocare aggiunge una riga, non cancella la precedente."
    >
      {CONSENT_KINDS.map((item) => {
        const entry = current.get(item)
        return (
          <div key={item} className="flex items-center justify-between gap-2 py-1 text-sm">
            <span>{CONSENT_LABELS[item]}</span>
            {entry === undefined ? (
              <Badge variant="neutral">mai raccolto</Badge>
            ) : (
              <span className="flex items-center gap-2">
                <Badge variant={entry.granted ? 'success' : 'danger'}>
                  {entry.granted ? 'concesso' : 'revocato'}
                </Badge>
                <span className="text-muted-foreground text-xs">
                  {formatDate(entry.occurredAt)} · v{entry.policyVersion}
                </span>
              </span>
            )}
          </div>
        )
      })}

      {/* «Mai raccolto» non è «negato», e la differenza conta: il primo significa che
          nessuno l'ha chiesto, il secondo che qualcuno ha detto no. */}
      {!disabled ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <Field label="Tipo">
            {(props) => (
              <Select
                {...props}
                value={kind}
                onValueChange={(v) => setKind(v as typeof kind)}
                items={CONSENT_KINDS.map((k) => ({ value: k, label: CONSENT_LABELS[k] }))}
              />
            )}
          </Field>
          <Field label="Canale">
            {(props) => (
              <Select
                {...props}
                value={channel}
                onValueChange={(v) => setChannel(v as typeof channel)}
                items={CONSENT_CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABELS[c] }))}
              />
            )}
          </Field>
          <Field label="Versione informativa" hint="Es. 2026-06.">
            {(props) => (
              <Input
                {...props}
                value={policyVersion}
                onChange={(e) => setPolicyVersion(e.target.value)}
              />
            )}
          </Field>
          <div className="flex gap-2 sm:col-span-3">
            <Button
              size="sm"
              disabled={policyVersion.trim() === ''}
              loading={addConsent.isPending}
              onClick={() => record(true)}
            >
              <BadgeCheck />
              Registra concesso
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={policyVersion.trim() === ''}
              loading={addConsent.isPending}
              onClick={() => record(false)}
            >
              <ShieldOff />
              Registra revoca
            </Button>
          </div>
        </div>
      ) : null}

      {addConsent.isError ? (
        <Alert variant="danger" className="mt-2">
          {errorMessage(addConsent.error)}
        </Alert>
      ) : null}

      <ConsentHistory gymMemberId={member.id} />
    </Section>
  )
}

/**
 * Lo storico — `GET …/consents`.
 *
 * Sta dietro un pulsante e non aperto di default: la scheda porta già lo stato
 * corrente, e chi apre una persona al banco vuole sapere **se** il consenso c'è, non
 * quante volte è cambiato. La domanda «da quando?» arriva dopo, e quando arriva vuole
 * anche le righe revocate — che nello stato corrente non compaiono.
 */
function ConsentHistory({ gymMemberId }: { gymMemberId: string }) {
  const [open, setOpen] = useState(false)
  const history = useConsentHistory(gymMemberId, undefined, open)
  const rows = history.data?.pages.flatMap((page) => page.data) ?? []

  return (
    <div className="border-border mt-4 border-t pt-3">
      <Button variant="ghost" size="sm" onClick={() => setOpen((previous) => !previous)}>
        <History />
        {open ? 'Nascondi lo storico' : 'Mostra lo storico'}
      </Button>

      {open ? (
        <div className="mt-2">
          {history.isPending ? (
            <Skeleton className="h-16" />
          ) : history.isError ? (
            <Alert variant="danger">{errorMessage(history.error)}</Alert>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nessuna riga: mai raccolto nulla.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {rows.map((entry) => (
                <li key={entry.id}>
                  <Card
                    radius="md"
                    padding="sm"
                    className="flex items-center justify-between gap-2 py-2"
                  >
                    <span className="truncate text-sm">
                      {CONSENT_LABELS[entry.kind]}
                      <span className="text-muted-foreground">
                        {' '}
                        · {formatDateTime(entry.occurredAt)} · v{entry.policyVersion} ·{' '}
                        {CHANNEL_LABELS[entry.channel]}
                      </span>
                      {entry.collectedByName !== null ? (
                        <span className="text-muted-foreground"> · {entry.collectedByName}</span>
                      ) : null}
                    </span>
                    <Badge variant={entry.granted ? 'success' : 'danger'}>
                      {entry.granted ? 'concesso' : 'revocato'}
                    </Badge>
                  </Card>
                </li>
              ))}
            </ul>
          )}

          {history.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              loading={history.isFetchingNextPage}
              onClick={() => void history.fetchNextPage()}
            >
              Carica altro
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function SportCardSection({ member, disabled }: { member: GymMember; disabled: boolean }) {
  const { addSportCard, updateSportCard, removeSportCard } = useGymMemberMutations(member.id)
  const [body, setBody] = useState('')
  const [number, setNumber] = useState('')
  const [expiresOn, setExpiresOn] = useState('')
  const [editing, setEditing] = useState(false)

  const card = member.currentSportCard
  const expired =
    card?.expiresOn != null && new Date(card.expiresOn) < new Date(new Date().toDateString())

  return (
    <Section
      title="Tesseramento"
      description="Una riga per anno sportivo: «era tesserato quando si è fatto male?» è una domanda assicurativa."
    >
      {card === null ? (
        <p className="text-muted-foreground text-sm">Nessun tesseramento registrato.</p>
      ) : editing ? (
        <SportCardEditor
          card={card}
          pending={updateSportCard.isPending}
          error={updateSportCard.isError ? errorMessage(updateSportCard.error) : null}
          onCancel={() => setEditing(false)}
          onSave={(input) =>
            updateSportCard.mutate(
              { sportCardId: card.id, input },
              { onSuccess: () => setEditing(false) },
            )
          }
        />
      ) : (
        <>
          <Row label="Ente" value={card.body} />
          <Row label="Numero" value={card.number} />
          <Row
            label="Scadenza"
            value={card.expiresOn === null ? null : formatDate(card.expiresOn)}
          />
          {expired ? (
            <Alert variant="warning" className="mt-2">
              Scaduto: il varco nega l'ingresso finché non è rinnovato.
            </Alert>
          ) : null}
          {!disabled ? (
            <div className="mt-2 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil />
                Correggi
              </Button>
              {/* Rimuovere è per il tesseramento inserito sulla persona sbagliata, non
                  per «è scaduto»: una tessera scaduta è un fatto, e cancellarla toglie
                  la risposta alla domanda assicurativa che questa sezione esiste per
                  conservare. */}
              <Button
                variant="ghost"
                size="sm"
                loading={removeSportCard.isPending}
                onClick={() => removeSportCard.mutate(card.id)}
              >
                <Trash2 />
                Rimuovi
              </Button>
            </div>
          ) : null}
          {removeSportCard.isError ? (
            <Alert variant="danger" className="mt-2">
              {errorMessage(removeSportCard.error)}
            </Alert>
          ) : null}
        </>
      )}

      {!disabled && !editing ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <Field label="Ente">
            {(props) => (
              <Input
                {...props}
                value={body}
                placeholder="CSEN"
                onChange={(e) => setBody(e.target.value)}
              />
            )}
          </Field>
          <Field label="Numero">
            {(props) => (
              <Input {...props} value={number} onChange={(e) => setNumber(e.target.value)} />
            )}
          </Field>
          <Field label="Scadenza">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-end">
            <Button
              size="sm"
              disabled={body.trim() === '' || number.trim() === ''}
              loading={addSportCard.isPending}
              onClick={() =>
                addSportCard.mutate(
                  {
                    body: body.trim(),
                    number: number.trim(),
                    expiresOn: expiresOn === '' ? undefined : expiresOn,
                  },
                  {
                    onSuccess: () => {
                      setBody('')
                      setNumber('')
                      setExpiresOn('')
                    },
                  },
                )
              }
            >
              <IdCard />
              Registra
            </Button>
          </div>
        </div>
      ) : null}
    </Section>
  )
}

/**
 * La correzione di un tesseramento — `PATCH …/sport-cards/{id}`.
 *
 * Le date sono nullabili nel contract, e qui la differenza è vera: svuotare il campo
 * della scadenza significa «senza scadenza», non «lascia com'era». Manda quindi `null`
 * e non stringa vuota, che il server rifiuterebbe come data non valida.
 */
function SportCardEditor({
  card,
  pending,
  error,
  onSave,
  onCancel,
}: {
  card: GymMemberSportCard
  pending: boolean
  error: string | null
  onSave: (input: { body: string; number: string; expiresOn: string | null }) => void
  onCancel: () => void
}) {
  const [body, setBody] = useState(card.body)
  const [number, setNumber] = useState(card.number)
  const [expiresOn, setExpiresOn] = useState(card.expiresOn ?? '')

  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
      <Field label="Ente">
        {(props) => <Input {...props} value={body} onChange={(e) => setBody(e.target.value)} />}
      </Field>
      <Field label="Numero">
        {(props) => <Input {...props} value={number} onChange={(e) => setNumber(e.target.value)} />}
      </Field>
      <Field label="Scadenza" hint="Vuoto: senza scadenza.">
        {(props) => (
          <Input
            {...props}
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
        )}
      </Field>
      <div className="flex items-end gap-2">
        <Button
          size="sm"
          disabled={body.trim() === '' || number.trim() === ''}
          loading={pending}
          onClick={() =>
            onSave({
              body: body.trim(),
              number: number.trim(),
              expiresOn: expiresOn === '' ? null : expiresOn,
            })
          }
        >
          Salva
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Annulla
        </Button>
      </div>
      {error !== null ? (
        <Alert variant="danger" className="sm:col-span-4">
          {error}
        </Alert>
      ) : null}
    </div>
  )
}

/**
 * La credenziale di accesso — QR-01, QR-04, e il codice di collegamento.
 *
 * Tre segreti diversi escono da qui, e **nessuno dei tre torna**: il segreto della
 * tessera, il token del cartoncino stampato, il codice di collegamento. Restano nello
 * stato locale finché la schermata è aperta e poi sono perduti — chi li perde ne
 * genera altri, che è meno grave del contrario.
 */
function CredentialSection({ member, disabled }: { member: GymMember; disabled: boolean }) {
  const credential = useAccessCredential(member.id)
  const { rotate, issuePrinted, revokePrinted, createClaimCode } = useCredentialMutations(member.id)

  const [secret, setSecret] = useState<GymMemberAccessCredentialSecret | null>(null)
  const [printed, setPrinted] = useState<PrintedCredentialToken | null>(null)
  const [claim, setClaim] = useState<GymMemberClaimCode | null>(null)

  const missing =
    credential.isError && credential.error instanceof ApiError && credential.error.status === 404

  return (
    <Section
      title="Tessera e accesso"
      description="Il QR si genera sul telefono dell'iscritto: qui si crea, si ruota e si stampa."
    >
      {credential.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : missing ? (
        <p className="text-muted-foreground text-sm">
          Nessuna credenziale: l'iscritto non può ancora entrare col QR.
        </p>
      ) : credential.data !== undefined ? (
        <>
          <Row label="Creata il" value={formatDate(credential.data.createdAt)} />
          <Row label="Ultima rotazione" value={formatDateTime(credential.data.rotatedAt)} />
          <Row label="Tessera stampata" value={credential.data.hasPrinted ? 'attiva' : 'nessuna'} />
        </>
      ) : null}

      {!disabled ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            loading={rotate.isPending}
            onClick={() => rotate.mutate(undefined, { onSuccess: (result) => setSecret(result) })}
          >
            <KeyRound />
            {missing ? 'Crea credenziale' : 'Ruota'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={missing}
            loading={issuePrinted.isPending}
            onClick={() =>
              issuePrinted.mutate(undefined, { onSuccess: (result) => setPrinted(result) })
            }
          >
            <Printer />
            Emetti tessera stampata
          </Button>
          {credential.data?.hasPrinted === true ? (
            <Button
              size="sm"
              variant="outline"
              loading={revokePrinted.isPending}
              onClick={() => revokePrinted.mutate()}
            >
              Revoca la stampata
            </Button>
          ) : null}
          {member.userId === null ? (
            <Button
              size="sm"
              variant="outline"
              loading={createClaimCode.isPending}
              onClick={() =>
                createClaimCode.mutate(undefined, { onSuccess: (result) => setClaim(result) })
              }
            >
              <Copy />
              Codice per l'app
            </Button>
          ) : null}
        </div>
      ) : null}

      {rotate.isError || issuePrinted.isError ? (
        <Alert variant="danger" className="mt-2">
          {errorMessage(rotate.error ?? issuePrinted.error)}
        </Alert>
      ) : null}

      {secret !== null ? (
        <SecretBox
          title="Segreto della tessera"
          value={`${secret.credentialRef}\n${secret.secret}`}
          hint="Va inquadrato o inserito nell'app dell'iscritto. Non tornerà: se si perde, si ruota di nuovo."
          onClose={() => setSecret(null)}
        />
      ) : null}

      {printed !== null ? (
        <SecretBox
          title="Tessera stampabile"
          value={printed.printedToken}
          hint="⚠️ Statica: una fotografia del cartoncino vale come il cartoncino, finché non lo revochi."
          onClose={() => setPrinted(null)}
        />
      ) : null}

      {claim !== null ? (
        <SecretBox
          title="Codice di collegamento"
          value={claim.code}
          hint={`Da dettare all'iscritto: lo inserisce nell'app e collega la tessera al suo account. Scade il ${formatDateTime(claim.expiresAt)}.`}
          onClose={() => setClaim(null)}
        />
      ) : null}
    </Section>
  )
}

/**
 * Mostra un segreto una volta sola, e **non si chiude da sé**: la chiusura è un clic
 * esplicito, come per i codici di recupero del secondo fattore. Un riquadro che
 * sparisce al primo clic fuori è il modo di perdere una cosa che non torna.
 */
function SecretBox({
  title,
  value,
  hint,
  onClose,
}: {
  title: string
  value: string
  hint: string
  onClose: () => void
}) {
  return (
    <div className="border-primary/40 bg-primary/5 mt-3 rounded-md border p-3">
      <p className="text-sm font-semibold">{title}</p>
      <pre className="bg-surface-alt mt-2 overflow-x-auto rounded p-2 font-mono text-xs whitespace-pre-wrap">
        {value}
      </pre>
      <p className="text-muted-foreground mt-2 text-xs">{hint}</p>
      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => void navigator.clipboard?.writeText(value)}
        >
          <Copy />
          Copia
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Ho finito
        </Button>
      </div>
    </div>
  )
}

function AccessesSection({ gymMemberId }: { gymMemberId: string }) {
  // Trenta giorni: la sezione risponde a «quando è venuto l'ultima volta?», non è il
  // registro completo — quello sta nella pagina degli accessi, con i suoi filtri. La
  // finestra si dichiara in giorni perché l'istante lo calcola la queryFn: `Date.now()`
  // durante il render è impuro, e renderebbe la chiave di query instabile.
  const events = useMemberAccessEvents(gymMemberId, 30, 10)

  return (
    <Section title="Accessi" description="Gli ultimi trenta giorni, tentativi negati compresi.">
      {events.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (events.data?.data.length ?? 0) === 0 ? (
        <EmptyState icon={<IdCard />} title="Nessun accesso" description="Non è ancora passato." />
      ) : (
        <ul className="flex flex-col gap-2">
          {(events.data?.data ?? []).map((event) => (
            <li key={event.id}>
              <Card
                radius="md"
                padding="sm"
                className="flex items-center justify-between gap-2 py-2"
              >
                <span className="text-sm">
                  {formatDateTime(event.occurredAt)}
                  {event.gate !== null ? (
                    <span className="text-muted-foreground"> · {event.gate}</span>
                  ) : null}
                </span>
                <Badge variant={event.outcome === 'allowed' ? 'success' : 'danger'}>
                  {event.outcome === 'allowed'
                    ? event.direction === 'in'
                      ? 'entrato'
                      : 'uscito'
                    : 'negato'}
                </Badge>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/**
 * I quattro generi in parole. `refund` e `reversal` sono **due fatti diversi** e non un tipo con due
 * segni: «gli è stato restituito» e «la ricarica non è mai valsa» non sono la stessa cosa da
 * spiegare a chi guarda il proprio registro.
 */
const WALLET_KIND_LABELS: Record<WalletMovementKind, string> = {
  topup: 'ricarica',
  spend: 'spesa',
  refund: 'rimborso',
  reversal: 'ricarica annullata',
}

/**
 * Il borsellino — PAY-07.
 *
 * ## Il saldo è grande, il registro è sotto
 *
 * Chi apre questa sezione ha una domanda sola: **quanto ha**. La risposta è la prima cosa che si
 * legge, e il registro spiega come ci si è arrivati — che è la seconda domanda, e si fa solo quando
 * la prima ha dato un numero che non torna.
 *
 * ## Non c'è un pulsante «ricarica», e non è una mancanza
 *
 * Una ricarica **è una vendita** di un prodotto `credit`, e si fa dal banco come qualunque altra: un
 * pulsante qui sarebbe una seconda strada per scrivere lo stesso fatto, cioè un secondo modo di
 * sbagliare l'importo — la ragione per cui il lettore di barcode aggiunge la riga con la funzione
 * del catalogo. E non c'è un campo per **correggere** il saldo, che sarebbe la facoltà di creare
 * denaro con un clic: si annulla la vendita che ha ricaricato.
 */
function WalletSection({ gymMemberId }: { gymMemberId: string }) {
  const wallet = useMemberWallet(gymMemberId)
  const movements = useMemberWalletMovements(gymMemberId)
  const balance = wallet.data?.balanceCents ?? 0

  return (
    <Section
      title="Credito interno"
      description="Denaro già incassato di cui resta un saldo. Si ricarica vendendo una ricarica al banco."
    >
      {wallet.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <>
          <p className="mb-3 text-2xl font-semibold tabular-nums">
            {formatMoney(balance, wallet.data?.currency ?? 'EUR')}
          </p>
          {(movements.data?.data.length ?? 0) === 0 ? (
            <EmptyState
              icon={<Wallet />}
              title="Nessun movimento"
              description="Questa persona non ha mai ricaricato."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {(movements.data?.data ?? []).map((movement) => (
                <li key={movement.id}>
                  <Card
                    radius="md"
                    padding="sm"
                    className="flex items-center justify-between gap-2 py-2"
                  >
                    <span className="text-sm">
                      {formatDateTime(movement.occurredAt)}
                      <span className="text-muted-foreground">
                        {' · '}
                        {WALLET_KIND_LABELS[movement.kind]}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {/* Il segno si legge dal numero **e** dal colore: il colore da solo non è
                        un'informazione accessibile. */}
                      <span
                        className={
                          movement.deltaCents > 0
                            ? 'text-success-content text-sm font-medium tabular-nums'
                            : 'text-danger-content text-sm font-medium tabular-nums'
                        }
                      >
                        {movement.deltaCents > 0 ? '+' : '−'}
                        {formatMoney(Math.abs(movement.deltaCents), wallet.data?.currency ?? 'EUR')}
                      </span>
                      {/* Il saldo dopo, perché «−15» da solo non dice se ce n'erano venti o duecento. */}
                      <span className="text-muted-foreground text-xs tabular-nums">
                        → {formatMoney(movement.balanceAfterCents, wallet.data?.currency ?? 'EUR')}
                      </span>
                    </span>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Section>
  )
}

/**
 * ANA-09. Sta in fondo, da sola, e chiede il cognome scritto a mano: è l'unica
 * operazione della scheda che non si annulla.
 */
function AnonymizeSection({ member }: { member: GymMember }) {
  const { anonymize } = useGymMemberMutations(member.id)
  const [confirm, setConfirm] = useState('')
  const [reason, setReason] = useState('')
  const [open, setOpen] = useState(false)

  return (
    <section className="border-danger/40 mt-4 rounded-lg border p-4">
      <h2 className="text-danger-content font-semibold">Anonimizza</h2>
      <p className="text-muted-foreground mt-1 text-xs">
        L'identità sparisce; iscrizioni e pagamenti restano, perché vanno conservati per dieci anni.
        Non è una cancellazione e non torna indietro.
      </p>

      {!open ? (
        <Button variant="outline" size="sm" className="mt-3" onClick={() => setOpen(true)}>
          <UserRoundX />
          Anonimizza questa anagrafica
        </Button>
      ) : (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <Field label="Scrivi il cognome per confermare">
            {(props) => (
              <Input
                {...props}
                value={confirm}
                placeholder={member.lastName}
                onChange={(e) => setConfirm(e.target.value)}
              />
            )}
          </Field>
          <Field label="Motivo" hint="Resta nel registro delle azioni.">
            {(props) => (
              <Input {...props} value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
          </Field>
          <div className="flex items-end gap-2">
            <Button
              size="sm"
              disabled={confirm.trim() === ''}
              loading={anonymize.isPending}
              onClick={() =>
                anonymize.mutate({
                  confirmLastName: confirm.trim(),
                  reason: reason.trim() === '' ? undefined : reason.trim(),
                })
              }
            >
              Confermo
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Annulla
            </Button>
          </div>
        </div>
      )}

      {anonymize.isError ? (
        <Alert variant="danger" className="mt-2">
          {errorMessage(anonymize.error)}
        </Alert>
      ) : null}
    </section>
  )
}

/**
 * I documenti assegnati — TRN-04 e NUT-02.
 *
 * ## Il tipo non è un'etichetta
 *
 * Da esso dipende il **permesso**: una scheda in PDF è una scheda, un piano è un
 * piano. Il selettore mostra quindi i soli tipi che chi guarda può **scrivere**, e
 * l'elenco arriva già ristretto a quelli che può **leggere** — il server lo fa, e
 * questa pagina non lo rifà: due filtri sullo stesso criterio si scoprono diversi
 * quando uno dei due cambia.
 *
 * ## Aprire, non scaricare
 *
 * Il contenuto si apre in una scheda del browser (`inline`), che per un PDF è il
 * gesto giusto: chi controlla una scheda non vuole un file nella cartella dei
 * download. L'URL creato da `createObjectURL` non si revoca subito — la scheda
 * appena aperta lo sta ancora leggendo — e muore con la pagina.
 *
 * ## Cancellare è vero
 *
 * Il testo lo dice prima del clic: un file caricato per errore può contenere i dati
 * di un'altra persona, e questa è la ragione per cui qui non c'è un'archiviazione.
 */
function DocumentsSection({ gymMemberId, disabled }: { gymMemberId: string; disabled: boolean }) {
  const permissions = useCurrentPermissions()
  const readable = MEMBER_DOCUMENT_KINDS.filter((kind) =>
    permissions.has(DOCUMENT_PERMISSIONS[kind].read),
  )
  const writable = MEMBER_DOCUMENT_KINDS.filter((kind) =>
    permissions.has(DOCUMENT_PERMISSIONS[kind].write),
  )
  const list = useMemberDocuments(gymMemberId, readable.length > 0)
  const mutations = useMemberDocumentMutations(gymMemberId)
  const [kind, setKind] = useState<MemberDocumentKind>(writable[0] ?? 'other')
  const [title, setTitle] = useState('')
  const [visible, setVisible] = useState(true)
  const [file, setFile] = useState<File | null>(null)

  if (readable.length === 0) {
    return (
      <Section title="Documenti" description="Schede e piani in PDF assegnati a questa persona.">
        <p className="text-muted-foreground text-sm">
          Il tuo ruolo non legge nessuno dei tipi di documento. È il permesso di chi scrive le
          schede o i piani, non del banco.
        </p>
      </Section>
    )
  }

  async function upload(): Promise<void> {
    if (file === null) {
      notify({ message: 'Scegli un file.', variant: 'danger' })
      return
    }
    try {
      await mutations.upload.mutateAsync({
        gymMemberId,
        file,
        kind,
        title,
        visibleToMember: visible,
      })
      setFile(null)
      setTitle('')
      notify({ message: 'Documento assegnato.', variant: 'success' })
    } catch (cause) {
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  async function open(document: MemberDocument): Promise<void> {
    try {
      const { blob } = await mutations.download.mutateAsync({
        id: document.id,
        filename: document.title,
      })
      window.open(URL.createObjectURL(blob), '_blank', 'noopener')
    } catch (cause) {
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  const rows = list.data?.data ?? []

  return (
    <Section
      title="Documenti"
      description="Schede e piani in PDF assegnati a questa persona. Il tipo decide chi può consegnarli e chi può aprirli."
    >
      {list.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nessun documento fra quelli che puoi vedere.
        </p>
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((document) => (
            <li key={document.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{document.title}</p>
                <p className="text-muted-foreground text-xs">
                  {DOCUMENT_KIND_LABELS[document.kind]} ·{' '}
                  {Math.max(1, Math.round(document.sizeBytes / 1024))} KB
                  {document.uploadedByName === null ? '' : ` · ${document.uploadedByName}`}
                  {document.visibleToMember ? '' : ' · non visibile all’iscritto'}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => void open(document)}>
                <Download className="size-4" />
                Apri
              </Button>
              {permissions.has(DOCUMENT_PERMISSIONS[document.kind].write) && !disabled ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void mutations.update
                        .mutateAsync({
                          id: document.id,
                          patch: { visibleToMember: !document.visibleToMember },
                        })
                        .catch((cause: unknown) => {
                          notify({ message: errorMessage(cause), variant: 'danger' })
                        })
                    }
                  >
                    {document.visibleToMember ? 'Nascondi' : 'Mostra'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void mutations.remove
                        .mutateAsync(document.id)
                        .then(() => {
                          notify({ message: 'Documento cancellato.', variant: 'success' })
                        })
                        .catch((cause: unknown) => {
                          notify({ message: errorMessage(cause), variant: 'danger' })
                        })
                    }
                  >
                    Cancella
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {writable.length > 0 && !disabled ? (
        <div className="border-border mt-3 space-y-2 border-t pt-3">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Tipo" className="w-44">
              {(props) => (
                <Select
                  {...props}
                  value={kind}
                  onValueChange={(value) => setKind(value as typeof kind)}
                  aria-label="Tipo di documento"
                  items={writable.map((value) => ({
                    value,
                    label: DOCUMENT_KIND_LABELS[value],
                  }))}
                />
              )}
            </Field>
            <Field label="Titolo" className="min-w-40 flex-1" hint="Vuoto: il nome del file.">
              {(props) => (
                <Input
                  {...props}
                  value={title}
                  maxLength={200}
                  onChange={(event) => setTitle(event.target.value)}
                />
              )}
            </Field>
            <Field label="File" className="w-56" hint="PDF o immagine, fino a 10 MB.">
              {(props) => (
                <Input
                  {...props}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/heic"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              )}
            </Field>
          </div>
          <Checkbox
            className="text-sm"
            checked={visible}
            onCheckedChange={setVisible}
            label="Visibile all’iscritto nell’app"
          />
          <Button
            size="sm"
            variant="primary"
            loading={mutations.upload.isPending}
            onClick={() => void upload()}
          >
            Assegna il documento
          </Button>
          <p className="text-muted-foreground text-xs">
            La cancellazione è definitiva: un documento non si archivia, perché un file caricato per
            errore può contenere i dati di un’altra persona.
          </p>
        </div>
      ) : null}
    </Section>
  )
}
